import {
	addUnit,
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
} from "@sembark-travel/datetime-utils"
import {
	Badge,
	Box,
	Button,
	Container,
	Heading,
	Icons,
	Inline,
	SelectInput,
	Stack,
	Table,
	Text,
} from "@sembark-travel/ui-base"
import { SelectInputField } from "@sembark-travel/ui-form"
import {
	areAdvancedFiltersAppliedDefault,
	ListView,
	Search,
	type TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { Link, useLocationQuery } from "@sembark-travel/ui-router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { generatePath } from "../router-utils"

type TIntervalType = "day" | "week" | "month"

type TTenantStatRow = {
	tenant: { id: number; name: string }
	total_credits: number
	send_credits: number
	send_count: number
	send_enabled: boolean
	parse_credits: number
	parse_count: number
	parse_enabled: boolean
	itinerary_builder_credits: number
	itinerary_builder_count: number
	itinerary_builder_enabled: boolean
	itinerary_parse_credits: number
	itinerary_parse_count: number
	itinerary_parse_enabled: boolean
	count: number
	balance: number
}

type TSummary = {
	tenant_count: number
	total_credits: number
	count: number
	send_credits: number
	send_count: number
	parse_credits: number
	parse_count: number
	itinerary_builder_credits: number
	itinerary_builder_count: number
	itinerary_parse_credits: number
	itinerary_parse_count: number
}

type TAlertState = "exhausted" | "low" | "healthy"

type TConsumption = "with" | "without"

/**
 * Feature flags the per-type columns are gated on, as the API names them.
 * Picking one narrows the listing to tenants that still have it switched on.
 */
const FEATURES = [
	{ value: "email_integration", label: "Email Integration" },
	{ value: "ai_itinerary_builder", label: "AI Itinerary Builder" },
	{ value: "trip_quote_parsing", label: "Package Uploader" },
] as const

type TFeature = (typeof FEATURES)[number]["value"]

type TFilters = TSearchParams & {
	intervalType: TIntervalType
	after: Date
	before: Date
	has_consumption?: TConsumption
	alert_state?: TAlertState
	feature?: TFeature
}

type TFiltersInQuery = TSearchParams & {
	intervalType: TIntervalType
	after: string
	before: string
	has_consumption?: TConsumption
	alert_state?: TAlertState
	feature?: TFeature
}

function Stat({
	label,
	value,
	hint,
}: {
	label: string
	value: string
	hint?: string
}) {
	return (
		<Stack gap="2">
			<Text fontWeight="semibold">{label}</Text>
			<Stack gap="px">
				<Text fontSize="xl" fontWeight="semibold">
					{value}
				</Text>
				{hint ? (
					<Text fontSize="xs" color="muted">
						{hint}
					</Text>
				) : null}
			</Stack>
		</Stack>
	)
}

function usages(count: number) {
	return `${count} ${count === 1 ? "usage" : "usages"}`
}

/**
 * A credits-consumed cell: the credits, with the number of consumptions that
 * produced them underneath. A tenant with no consumption of that type renders
 * the zero without a count.
 *
 * `enabled` is the tenant's feature flag for whatever spends these credits.
 * When it is off the cell is marked with a ban icon — past spend stays on the
 * report after the integration is switched off, and the two read identically
 * otherwise.
 */
function Usage({
	credits,
	count,
	enabled,
	label,
	bold,
}: {
	credits: number
	count: number
	enabled?: boolean
	label?: string
	bold?: boolean
}) {
	return (
		<Stack gap="px">
			<Inline gap="1" alignItems="center" justifyContent="end">
				{enabled === false ? (
					<Icons.Ban
						color="warning"
						size="3"
						title={`${label} is disabled for this tenant`}
					/>
				) : null}
				<Text fontSize="sm" fontWeight={bold ? "semibold" : undefined}>
					{credits.toFixed(2)}
				</Text>
			</Inline>
			{count ? (
				<Text fontSize="xs" color="muted">
					{usages(count)}
				</Text>
			) : null}
		</Stack>
	)
}

/**
 * Cross-tenant "Tenant-wise" credit-consumption view for accounting admins:
 * per-tenant credits consumed in the selected interval (total + Email Send / AI
 * Parsing / Itinerary Builder / Itinerary Parser split + count) and each
 * tenant's live wallet balance, ordered by spend. Backed by
 * /subscription-wallet-credit-usages/tenant-stats.
 */
export function TenantCreditStatsView() {
	const xhr = useXHR()
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)

	const requestParams = useMemo(
		() => ({
			page: params.page || 1,
			q: params.q || undefined,
			after: dateToUTCString(startOf(params.after, "day")),
			before: dateToUTCString(endOf(params.before, "day")),
			has_consumption: params.has_consumption || undefined,
			alert_state: params.alert_state || undefined,
			feature: params.feature || undefined,
		}),
		[params]
	)

	const statsParams = useMemo(() => {
		const { page: _page, ...rest } = requestParams
		return { ...rest, limit: 1 }
	}, [requestParams])

	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	const { data: summaryResp } = useSWR<{ summary: TSummary }>(
		["tenant-credit-summary", statsParams],
		() =>
			xhr
				.get("/subscription-wallet-credit-usages/tenant-stats", {
					params: statsParams,
				})
				.then((resp) => resp.data)
	)
	const summary = summaryResp?.summary

	return (
		<Search
			title="Tenant-wise Consumption"
			initialParams={params}
			onSearch={setParams}
			placeholder="Search tenant"
			Filters={Filters}
			resetParams={(p) => ({
				q: "",
				page: 1,
				after: p.after,
				before: p.before,
				intervalType: p.intervalType,
				has_consumption: undefined,
				alert_state: undefined,
				feature: undefined,
			})}
			areAdvancedFiltersApplied={(params) => {
				const { after, before, intervalType, ...others } = params
				return areAdvancedFiltersAppliedDefault(others)
			}}
			actions={({ setSearchParams, searchParams }) => (
				<Inline alignItems="center" gap="2">
					<Button
						onClick={() => {
							const after = startOf(
								subtractUnit(searchParams.after, 1, searchParams.intervalType),
								searchParams.intervalType
							)
							setSearchParams({
								after,
								before: endOf(after, searchParams.intervalType),
								page: 1,
							})
						}}
					>
						<Icons.ChevronDown rotate="90" />
					</Button>
					<Box>
						<SelectInput
							value={searchParams.intervalType}
							onChange={(e) => {
								const type = (e.currentTarget.value || "month") as TIntervalType
								setSearchParams({
									intervalType: type,
									after: startOf(new Date(), type),
									before: endOf(new Date(), type),
									page: 1,
								})
							}}
						>
							<option value="month">Month</option>
							<option value="week">Week</option>
							<option value="day">Day</option>
						</SelectInput>
					</Box>
					<Button
						onClick={() => {
							const after = startOf(
								addUnit(searchParams.after, 1, searchParams.intervalType),
								searchParams.intervalType
							)
							setSearchParams({
								after,
								before: endOf(after, searchParams.intervalType),
								page: 1,
							})
						}}
					>
						<Icons.ChevronDown rotate="270" />
					</Button>
				</Inline>
			)}
		>
			<Container paddingY="4">
				<Stack gap="4">
					<Heading as="h5" fontSize="lg">
						{formatDate(params.after, "ddd D MMM")} -{" "}
						{formatDate(params.before, "ddd D MMM")}
					</Heading>
					<Inline
						gap="8"
						flexWrap="wrap"
						alignItems="start"
						bgColor="default"
						padding="4"
						rounded="md"
						borderWidth="1"
					>
						<Stat
							label="Tenants"
							value={!summary ? "..." : String(summary.tenant_count)}
						/>
						<Stat
							label="Credits Used"
							value={!summary ? "..." : summary.total_credits.toFixed(2)}
							hint={summary ? usages(summary.count) : undefined}
						/>
						<Stat
							label="Email Send"
							value={!summary ? "..." : summary.send_credits.toFixed(2)}
							hint={summary ? usages(summary.send_count) : undefined}
						/>
						<Stat
							label="AI Parsing"
							value={!summary ? "..." : summary.parse_credits.toFixed(2)}
							hint={summary ? usages(summary.parse_count) : undefined}
						/>
						<Stat
							label="Itinerary Builder"
							value={
								!summary ? "..." : summary.itinerary_builder_credits.toFixed(2)
							}
							hint={
								summary ? usages(summary.itinerary_builder_count) : undefined
							}
						/>
						<Stat
							label="Itinerary Parser"
							value={
								!summary ? "..." : summary.itinerary_parse_credits.toFixed(2)
							}
							hint={summary ? usages(summary.itinerary_parse_count) : undefined}
						/>
					</Inline>
				</Stack>
			</Container>
			<ListView<TTenantStatRow, TFilters>
				params={params}
				pageKey="admin-tenant-credit-stats"
				fetch={(api) =>
					api
						.get("/subscription-wallet-credit-usages/tenant-stats", {
							params: requestParams,
						})
						.then((resp) => resp.data)
				}
				onPageChange={(page) => setParams({ ...params, page })}
			>
				{({ items }) => (
					<Table
						headers={[
							"Tenant",
							"Email Send",
							"AI Parsing",
							"Itinerary Builder",
							"Itinerary Parser",
							"Total",
							"Balance",
						]}
						bordered
						hover
						responsive
						alignCols={{
							1: "right",
							2: "right",
							3: "right",
							4: "right",
							5: "right",
							6: "right",
						}}
						rows={items.map((row) => [
							<Link
								to={`${generatePath("/admin/credit-consumption/all")}?tenant_id=${row.tenant.id}`}
								fontWeight="semibold"
							>
								{row.tenant.name}
							</Link>,
							<Usage
								credits={row.send_credits}
								count={row.send_count}
								enabled={row.send_enabled}
								label="Email Integration"
							/>,
							<Usage
								credits={row.parse_credits}
								count={row.parse_count}
								enabled={row.parse_enabled}
								label="Email Integration"
							/>,
							<Usage
								credits={row.itinerary_builder_credits}
								count={row.itinerary_builder_count}
								enabled={row.itinerary_builder_enabled}
								label="AI Itinerary Builder"
							/>,
							<Usage
								credits={row.itinerary_parse_credits}
								count={row.itinerary_parse_count}
								enabled={row.itinerary_parse_enabled}
								label="Package Uploader"
							/>,
							<Usage credits={row.total_credits} count={row.count} bold />,
							row.balance <= 0 ? (
								<Badge status="danger">{row.balance.toFixed(2)}</Badge>
							) : (
								<Text fontSize="sm">{row.balance.toFixed(2)}</Text>
							),
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<SelectInputField name="has_consumption" label="Show">
				<option value="">All tenants</option>
				<option value="with">With consumption</option>
				<option value="without">Without consumption</option>
			</SelectInputField>
			<SelectInputField name="alert_state" label="Wallet alert">
				<option value="">Any</option>
				<option value="exhausted">Exhausted</option>
				<option value="low">Low balance</option>
				<option value="healthy">Healthy</option>
			</SelectInputField>
			<SelectInputField name="feature" label="Feature enabled">
				<option value="">All features</option>
				{FEATURES.map(({ value, label }) => (
					<option key={value} value={value}>
						{label}
					</option>
				))}
			</SelectInputField>
		</Stack>
	)
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const intervalType = filters.intervalType || "month"
	const query: TFiltersInQuery = {
		intervalType,
		after: dateToQuery(filters.after || startOf(new Date(), intervalType)),
		before: dateToQuery(filters.before || endOf(new Date(), intervalType)),
	}
	if (filters.q) query.q = filters.q
	if (filters.page) query.page = filters.page
	if (filters.has_consumption) query.has_consumption = filters.has_consumption
	if (filters.alert_state) query.alert_state = filters.alert_state
	if (filters.feature) query.feature = filters.feature
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const intervalType = query.intervalType || "month"
	const filters: TFilters = {
		intervalType,
		after: query.after
			? startOf(parseDateFromQuery(query.after), "day")
			: startOf(new Date(), intervalType),
		before: query.before
			? endOf(parseDateFromQuery(query.before), "day")
			: endOf(new Date(), intervalType),
	}
	if (query.q) filters.q = query.q
	if (query.page) filters.page = query.page
	if (query.has_consumption) filters.has_consumption = query.has_consumption
	if (query.alert_state) filters.alert_state = query.alert_state
	if (query.feature) filters.feature = query.feature
	return filters
}
